import { Box } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

export const ScoreHeaderCnt = styled(Box)(({ theme }) => ({
  '& .MuiAccordionSummary-root': {
    backgroundColor: '#0000000A !important',
  },
  '& .summaryBar': {
    height: 82,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '16px',
  },
  '& .summaryBarLeft': {
    justifyContent: 'center',
    height: 'inherit',
    backgroundColor: '#FF8A65',
    borderTopLeftRadius: '12px',
    borderBottomLeftRadius: '12px',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: '150px',
  },
  '& .summaryBarLeftTitle': {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '20px',
    marginBottom: '4px',
  },
  '& .summaryBarLeftSubtitle': {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: '28px',
  },
  '& .summaryBarRight': {
    width: '100%',
    flex: 1,
    height: 'inherit',
    backgroundColor: '#FEF5E7',
    padding: '16px 24px',
    borderRadius: '0px 12px 12px 0px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '24px',
  },
  '& .summaryBarRightContent': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
  '& .overallScoreText': {
    fontWeight: 800,
    fontSize: '16px',
    color: '#513D00',
  },
  '& .overallScoreValue': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#000000',
    '& .value': {
      fontWeight: 800,
      fontSize: '16px',
      color: '#513D00',
    },
  },
  '& .tableContainer': {
    backgroundColor: '#FFFFFF',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },

  '& .tableHeader': {
    backgroundColor: '#F8F9FA',
    borderBottom: '2px solid #E0E0E0',
  },

  '& .table': {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },

  '& .tableHeaderCell': {
    padding: '12px 16px',
    fontWeight: 800,
    fontSize: '13px',
    color: '#212121',
    textAlign: 'center',
    borderRight: '1px solid #E0E0E0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
}));

// Styles object containing className-based styles
export const scoreSummaryStyles = {
  // Score Summary section
  scoreSummaryHeader: {
    backgroundColor: '#FFFFFF',
    padding: '16px 24px',
    borderBottom: '1px solid #E0E0E0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  scoreSummaryTitle: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#1A1A1A',
  },

  tableHeaderCell: {
    padding: '12px 16px',
    fontWeight: 600,
    fontSize: '13px',
    color: '#616161',
    textAlign: 'left' as const,
    borderRight: '1px solid #E0E0E0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  tableRow: (level: number) => ({
    backgroundColor: level === 0 ? '#FFFFFF' : '#FAFAFA',
    borderBottom: '1px solid #F0F0F0',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  }),
  tableCell: (level: number) => ({
    padding: '6px 10px',
    fontSize: '14px',
    color: '#212121',
    fontWeight: level === 0 ? 800 : 500,
    verticalAlign: 'middle',
    paddingLeft: level === 0 ? '16px' : `${16 + level * 24}px`,
    '&:last-child': {
      borderRight: 'none',
    },
  }),
  tableCellNoIndent: (level: number) => ({
    padding: '6px 10px',
    fontSize: '14px',
    color: '#212121',
    fontWeight: level === 0 ? 800 : 500,
    verticalAlign: 'middle',
    paddingLeft: '16px', // No indentation - always 16px regardless of level
    '&:last-child': {
      borderRight: 'none',
    },
  }),
  tableCellCenter: {
    textAlign: 'center' as const,
  },

  // Rating chip styles
  ratingChip: (percentage: number) => ({
    padding: '4px 12px',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: '80px',
    justifyContent: 'center',
  }),

  // Overall score chip
  overallScoreChip: (percentage: number) => ({
    padding: '6px 16px',
    borderRadius: '20px',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  }),

  // Empty state
  emptyState: (theme: Theme) => ({
    padding: theme.spacing(4),
    textAlign: 'center' as const,
    color: '#757575',
    backgroundColor: '#FFFFFF',
  }),
  contentContainer: {
    backgroundColor: '#FFFFFF',
  },
};

// CSS class names for conditional styling
export const scoreClassNames = {
  headerContainer: 'header-container',
  overallScoreContainer: 'overall-score-container',
  scoreSummaryHeader: 'score-summary-header',
  tableContainer: 'table-container',
  table: 'table',
  tableHeader: 'table-header',
  tableHeaderCell: 'table-header-cell',
  tableRow: 'table-row',
  tableCell: 'table-cell',
  tableCellCenter: 'table-cell-center',
  ratingChip: 'rating-chip',
  overallScoreChip: 'overall-score-chip',
  emptyState: 'empty-state',

  // Legacy class names
  scoreContainer: 'score-container',
  scoreRow: 'score-row',
  scoreLabel: 'score-label',
  percentageChip: 'percentage-chip',
  pointsText: 'points-text',
  contentContainer: 'content-container',
  divider: 'divider',
};
